/**
 * Edit Character page - allows owner to edit their character
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function EditCharacter() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user, language, updateUser } = React.useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [character, setCharacter] = useState(null);
  
  // Form state
  const [appearance, setAppearance] = useState({});
  const [characterInfo, setCharacterInfo] = useState({});
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [hoverImageUrl, setHoverImageUrl] = useState(null);
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [mediaLoading, setMediaLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryType, setGalleryType] = useState(null); // 'avatar' or 'hover'
  const [hiddenMedia, setHiddenMedia] = useState(new Set()); // Track hidden media URLs
  const [hidingMedia, setHidingMedia] = useState(new Set()); // Track media being hidden/unhidden
  
  useEffect(() => {
    if (user && characterId) {
      loadCharacter();
    }
  }, [user, characterId]);
  
  const loadMedia = async () => {
    try {
      // Load media - owner will see all media including hidden ones
      const response = await axios.get(`/web/characters/${characterId}/media`);
      const mediaData = response.data.media || { images: [], videos: [] };
      setMedia(mediaData);
      
      // Get hidden media list from response (for owner)
      // Normalize URLs for consistent comparison
      if (response.data.hidden_media && Array.isArray(response.data.hidden_media)) {
        const normalizedHidden = new Set();
        response.data.hidden_media.forEach(url => {
          let normalized = url;
          if (normalized.startsWith('/')) {
            normalized = normalized.slice(1);
          }
          normalizedHidden.add(normalized);
          // Also add variations for matching
          normalizedHidden.add(url);
          normalizedHidden.add(`/${normalized}`);
        });
        setHiddenMedia(normalizedHidden);
        console.log('Loaded hidden media:', Array.from(normalizedHidden));
      } else {
        setHiddenMedia(new Set());
      }
    } catch (err) {
      console.error('Failed to load media:', err);
      setMedia({ images: [], videos: [] });
    } finally {
      setMediaLoading(false);
    }
  };
  
  const handleHideMedia = async (mediaUrl, isHidden) => {
    if (hidingMedia.has(mediaUrl)) return; // Prevent double-click
    
    setHidingMedia(prev => new Set(prev).add(mediaUrl));
    
    try {
      // Normalize URL - remove leading slash and domain if present
      let normalizedUrl = mediaUrl;
      if (normalizedUrl.startsWith('http')) {
        // Extract path from full URL
        try {
          const urlObj = new URL(normalizedUrl);
          normalizedUrl = urlObj.pathname;
        } catch (e) {
          console.warn('Failed to parse URL:', normalizedUrl);
        }
      }
      // Remove leading slash for consistency with backend
      normalizedUrl = normalizedUrl.startsWith('/') ? normalizedUrl.slice(1) : normalizedUrl;
      
      console.log('Hiding/unhiding media:', { original: mediaUrl, normalized: normalizedUrl, isHidden });
      
      if (isHidden) {
        // Unhide media
        const response = await axios.post(`/web/characters/${characterId}/unhide_media`, {
          media_url: normalizedUrl
        });
        console.log('Unhide response:', response.data);
        setHiddenMedia(prev => {
          const newSet = new Set(prev);
          // Remove all variations of the URL
          newSet.delete(mediaUrl);
          newSet.delete(normalizedUrl);
          newSet.delete(`/${normalizedUrl}`);
          return newSet;
        });
      } else {
        // Hide media
        const response = await axios.post(`/web/characters/${characterId}/hide_media`, {
          media_url: normalizedUrl,
          reason: 'Hidden by owner'
        });
        console.log('Hide response:', response.data);
        setHiddenMedia(prev => {
          const newSet = new Set(prev);
          newSet.add(normalizedUrl);
          return newSet;
        });
      }
      
      // Reload media to get updated list
      await loadMedia();
    } catch (err) {
      console.error('Failed to hide/unhide media:', err);
      console.error('Error details:', err.response?.data);
      alert(language === 'en' 
        ? `Failed to update media visibility: ${err.response?.data?.detail || err.message}` 
        : `Не удалось обновить видимость медиа: ${err.response?.data?.detail || err.message}`);
    } finally {
      setHidingMedia(prev => {
        const newSet = new Set(prev);
        newSet.delete(mediaUrl);
        return newSet;
      });
    }
  };
  
  const loadCharacter = async () => {
    try {
      const response = await axios.get(`/web/characters/${characterId}`);
      const char = response.data;
      setCharacter(char);
      
      // Populate form with existing data
      setAppearance(char.appearance || {});
      setCharacterInfo({
        name: char.name,
        age: char.appearance?.age || char.personality?.age || '',
        greeting: '', // Will load from file
        scenario: char.personality?.scenario || '',
        public_description: char.personality?.description || char.personality?.description_en || '',
        tags: char.personality?.tags || []
      });
      setGenerationPrompt(char.personality?.generation_prompt || '');
      setAvatarUrl(char.avatar_url);
      setHoverImageUrl(char.personality?.hover_image_url || null);
      
      // Load media gallery
      loadMedia();
      
      // Load greeting from file
      try {
        const greetingResponse = await axios.get(`/web/characters/${characterId}/greeting?language=${language}`);
        if (greetingResponse.data.greeting) {
          setCharacterInfo(prev => ({ ...prev, greeting: greetingResponse.data.greeting }));
        }
      } catch (e) {
        console.log('Could not load greeting:', e);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load character');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectImage = (imageUrl, type) => {
    if (type === 'avatar') {
      setAvatarUrl(imageUrl);
    } else if (type === 'hover') {
      setHoverImageUrl(imageUrl);
    }
    setShowGallery(false);
    setGalleryType(null);
  };
  
  const handleSave = async () => {
    if (!user || user.token_balance < 19) {
      setError(language === 'en' ? 'Insufficient tokens. Need 19 tokens to save changes.' : 'Недостаточно токенов. Нужно 19 токенов для сохранения изменений.');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const payload = {
        appearance: null, // Appearance is not editable
        character_info: {
          // Don't send name and age - they are not editable
          greeting: characterInfo.greeting || '',
          scenario: characterInfo.scenario || '',
          public_description: characterInfo.public_description || '',
          tags: characterInfo.tags || [],
          generation_prompt: generationPrompt || ''
        },
        generation_prompt: generationPrompt || '',
        avatar_url: avatarUrl || null,
        second_image_url: hoverImageUrl || null
      };
      
      console.log('Saving character edit:', payload);
      
      const response = await axios.post(`/web/characters/${characterId}/edit`, payload);
      console.log('Character edit response:', response.data);
      
      // Refresh user balance
      if (updateUser && user) {
        const userResponse = await axios.get('/auth/me');
        updateUser(userResponse.data);
      }
      
      // Show success message
      alert(language === 'en' ? 'Changes saved successfully!' : 'Изменения успешно сохранены!');
      
      navigate(`/web/characters`);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Failed to save changes' : 'Не удалось сохранить изменения'));
    } finally {
      setSaving(false);
    }
  };
  
  const translations = {
    en: {
      title: 'Edit Character',
      appearance: 'Appearance',
      characterInfo: 'Character Info',
      generation: 'Generation',
      individualCharacteristics: 'Individual Characteristics',
      changeAvatar: 'Change Avatar',
      changeHoverImage: 'Change Hover Image',
      selectFromGallery: 'Select from Gallery',
      mainImage: 'Main Image',
      hoverImage: 'Hover Image',
      save: 'Save Changes',
      saveCost: 'Cost: 19 tokens',
      saveNote: 'After saving, your character will go back to moderation and become private.',
      back: 'Back',
      loading: 'Loading...',
      name: 'Name',
      age: 'Age',
      greeting: 'Greeting',
      scenario: 'Scenario',
      publicDescription: 'Public Description',
      tags: 'Tags',
      tagsHint: 'Separate by space or comma (up to 5)'
    },
    ru: {
      title: 'Редактировать персонажа',
      appearance: 'Внешность',
      characterInfo: 'Информация о персонаже',
      generation: 'Генерация',
      individualCharacteristics: 'Индивидуальные характеристики',
      generateAvatar: 'Сгенерировать новый аватар',
      cost: 'Стоимость: 19 токенов',
      save: 'Сохранить изменения',
      saveCost: 'Стоимость: 19 токенов',
      saveNote: 'После сохранения ваш персонаж вернется на модерацию и станет приватным.',
      back: 'Назад',
      loading: 'Загрузка...',
      name: 'Имя',
      age: 'Возраст',
      greeting: 'Приветствие',
      scenario: 'Сценарий',
      publicDescription: 'Публичное описание',
      tags: 'Теги',
      tagsHint: 'Разделяйте пробелом или запятой (до 5)'
    }
  };
  
  const t = translations[language] || translations.en;
  
  if (!user) {
    return (
      <div className="text-white text-center py-12">
        {language === 'en' ? 'Please login' : 'Пожалуйста, войдите'}
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400">{t.loading}</div>
      </div>
    );
  }
  
  if (!character) {
    return (
      <div className="text-white text-center py-12">
        {language === 'en' ? 'Character not found' : 'Персонаж не найден'}
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto px-4 pb-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-white">{t.title}</h1>
        <button
          onClick={() => navigate('/web/characters')}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition"
        >
          {t.back}
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-600/20 border border-red-600/30 rounded text-red-400">
          {error}
        </div>
      )}
      
      {/* Character Info (without Name, Age, and Appearance) */}
      <div className="mb-8 bg-dark-800 border border-burgundy-800 rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4 text-burgundy-400">{t.characterInfo}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t.greeting}</label>
            <textarea
              value={characterInfo.greeting || ''}
              onChange={(e) => setCharacterInfo({ ...characterInfo, greeting: e.target.value })}
              className="w-full bg-dark-700 border border-burgundy-800 text-white rounded px-3 py-2 h-24"
              placeholder={language === 'en' ? 'First message from the character' : 'Первое сообщение от персонажа'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">{t.scenario}</label>
            <textarea
              value={characterInfo.scenario || ''}
              onChange={(e) => setCharacterInfo({ ...characterInfo, scenario: e.target.value })}
              className="w-full bg-dark-700 border border-burgundy-800 text-white rounded px-3 py-2 h-32"
              placeholder={language === 'en' ? 'System prompt scenario' : 'Сценарий системного промпта'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">{t.publicDescription}</label>
            <textarea
              value={characterInfo.public_description || ''}
              onChange={(e) => setCharacterInfo({ ...characterInfo, public_description: e.target.value })}
              className="w-full bg-dark-700 border border-burgundy-800 text-white rounded px-3 py-2 h-24"
              placeholder={language === 'en' ? 'Public description (ABOUT section)' : 'Публичное описание (раздел ABOUT)'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">{t.tags}</label>
            <input
              type="text"
              value={Array.isArray(characterInfo.tags) ? characterInfo.tags.join(', ') : (characterInfo.tags || '')}
              onChange={(e) => {
                const tagsStr = e.target.value;
                const tags = tagsStr.split(/\s*,?\s*|\s+/).filter(t => t.trim()).slice(0, 5);
                setCharacterInfo({ ...characterInfo, tags });
              }}
              className="w-full bg-dark-700 border border-burgundy-800 text-white rounded px-3 py-2"
              placeholder={t.tagsHint}
            />
            <p className="text-xs text-gray-400 mt-1">
              {language === 'en' ? 'Tags used: ' : 'Тегов использовано: '}
              {Array.isArray(characterInfo.tags) ? characterInfo.tags.length : 0}/5
            </p>
          </div>
        </div>
      </div>
      
      {/* Step 3: Images Selection */}
      <div className="mb-8 bg-dark-800 border border-burgundy-800 rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4 text-burgundy-400">{language === 'en' ? 'Images' : 'Изображения'}</h2>
        
        {/* Media Gallery Management */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-white">{language === 'en' ? 'Media Gallery' : 'Галерея медиа'}</h3>
          <button
            onClick={() => {
              setGalleryType('media');
              setShowGallery(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
          >
            {language === 'en' ? 'Manage Media Gallery' : 'Управление галереей'}
          </button>
        </div>
        
        {/* Main Image */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">{t.mainImage}</label>
          {avatarUrl && (
            <div className="mb-2">
              <img src={avatarUrl} alt="Main" className="max-w-xs rounded mb-2" />
            </div>
          )}
          <button
            onClick={() => {
              setGalleryType('avatar');
              setShowGallery(true);
            }}
            className="px-4 py-2 bg-burgundy-600 hover:bg-burgundy-700 text-white rounded-lg transition"
          >
            {t.changeAvatar}
          </button>
        </div>
        
        {/* Hover Image */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">{t.hoverImage}</label>
          {hoverImageUrl && (
            <div className="mb-2">
              <img src={hoverImageUrl} alt="Hover" className="max-w-xs rounded mb-2" />
            </div>
          )}
          <button
            onClick={() => {
              setGalleryType('hover');
              setShowGallery(true);
            }}
            className="px-4 py-2 bg-burgundy-600 hover:bg-burgundy-700 text-white rounded-lg transition"
          >
            {t.changeHoverImage}
          </button>
        </div>
      </div>
      
      {/* Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-800 border border-burgundy-800 rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">
                {galleryType === 'media' 
                  ? (language === 'en' ? 'Manage Media Gallery' : 'Управление галереей')
                  : t.selectFromGallery}
              </h3>
              <button
                onClick={() => {
                  setShowGallery(false);
                  setGalleryType(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                {language === 'en' ? 'Close' : 'Закрыть'}
              </button>
            </div>
            {mediaLoading ? (
              <div className="text-center py-8">{t.loading}</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {media.images.map((imageUrl, idx) => {
                  // Normalize URL for comparison
                  let normalizedUrl = imageUrl;
                  if (normalizedUrl.startsWith('http')) {
                    try {
                      const urlObj = new URL(normalizedUrl);
                      normalizedUrl = urlObj.pathname;
                    } catch (e) {
                      // Keep original if parsing fails
                    }
                  }
                  normalizedUrl = normalizedUrl.startsWith('/') ? normalizedUrl.slice(1) : normalizedUrl;
                  
                  // Check if hidden (check all variations)
                  const isHidden = hiddenMedia.has(imageUrl) || 
                                   hiddenMedia.has(normalizedUrl) || 
                                   hiddenMedia.has(`/${normalizedUrl}`);
                  const isHiding = hidingMedia.has(imageUrl) || hidingMedia.has(normalizedUrl);
                  
                  return (
                    <div
                      key={idx}
                      className={`relative aspect-[3/4] rounded-lg overflow-hidden border-2 ${
                        (galleryType === 'avatar' && avatarUrl === imageUrl) ||
                        (galleryType === 'hover' && hoverImageUrl === imageUrl)
                          ? 'border-burgundy-500 ring-2 ring-burgundy-500'
                          : isHidden
                          ? 'border-gray-600 opacity-50'
                          : 'border-burgundy-800 hover:border-burgundy-600'
                      }`}
                    >
                      {galleryType === 'media' ? (
                        <>
                          <img
                            src={imageUrl.startsWith('http') ? imageUrl : `https://hotbump.online${imageUrl}`}
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {isHidden && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {language === 'en' ? 'HIDDEN' : 'СКРЫТО'}
                              </span>
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleHideMedia(imageUrl, isHidden);
                            }}
                            disabled={isHiding}
                            className={`absolute top-2 right-2 px-2 py-1 text-xs rounded z-10 pointer-events-auto ${
                              isHidden
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            } disabled:opacity-50`}
                            title={isHidden 
                              ? (language === 'en' ? 'Show in gallery' : 'Показать в галерее')
                              : (language === 'en' ? 'Hide from gallery' : 'Скрыть из галереи')
                            }
                          >
                            {isHiding ? '...' : (isHidden ? '👁️' : '🚫')}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleSelectImage(imageUrl, galleryType)}
                          className="w-full h-full"
                        >
                          <img
                            src={imageUrl.startsWith('http') ? imageUrl : `https://hotbump.online${imageUrl}`}
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      )}
                    </div>
                  );
                })}
                {media.images.length === 0 && (
                  <div className="col-span-full text-center text-gray-400 py-8">
                    {language === 'en' ? 'No images in gallery' : 'Нет изображений в галерее'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="bg-dark-800 border border-burgundy-800 rounded-lg p-6">
        <button
          onClick={handleSave}
          disabled={saving || user.token_balance < 19}
          className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed mb-2"
        >
          {saving ? (language === 'en' ? 'Saving...' : 'Сохранение...') : t.save}
        </button>
        <p className="text-sm text-gray-400 mb-2">{t.saveCost}</p>
        <p className="text-sm text-yellow-400">{t.saveNote}</p>
      </div>
    </div>
  );
}

export default EditCharacter;

